import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_FLEET_LOCATE_APP, EnableFleetLocateAppInput } from '../data/mutations'

/**
 * Saves FleetLocate app's api key, username and password.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const FleetLocateModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableFleetLocateApp, { loading }] = useMutation<
    { enableFleetLocateApp: { apiKey: string; password: string; username: string } },
    EnableFleetLocateAppInput
  >(ENABLE_FLEET_LOCATE_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Fleet Locate app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect API key') {
        setFormError('Invalid api key or username/password. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid api key or username/password. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { apiKey: string; appPassword: string; appUsername: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableFleetLocateApp({
      variables: {
        apiKey: event.apiKey,
        appType: appType,
        password: event.appPassword,
        username: event.appUsername
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/fleet-locate"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin FleetLocate account. Enter your FleetLocate api key, username and password.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'FleetLocate Api Key',
            name: 'apiKey',

            placeHolder: 'Enter your Fleet Locate Api Key',
            type: 'input'
          },
          {
            labelText: 'FleetLocate username',
            name: 'appUsername',

            placeHolder: 'Enter your Fleet Locate username',
            type: 'input'
          },
          {
            labelText: 'FleetLocate password',
            name: 'appPassword',

            placeHolder: 'Enter your Fleet Locate password',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

FleetLocateModal.displayName = 'FleetLocateModal'

export { FleetLocateModal }
